import { env } from "@/lib/env"

const apiURL = env.apiUrl.replace(/\/$/, "")

async function publicFetch<T>(
  endpoint: string,
  options?: RequestInit,
): Promise<T> {
  const url = `${apiURL}/${endpoint.replace(/^\//, "")}`

  const headers = new Headers(options?.headers)
  if (options?.body != null && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json")
  }

  const response = await fetch(url, {
    ...options,
    headers,
  })

  if (!response.ok) {
    throw response
  }

  if (response.status === 204) {
    return null as T
  }

  const text = await response.text()
  if (!text) return null as T

  return JSON.parse(text) as T
}

// ── Types ────────────────────────────────────────────────

import type { TChangelogEntryWithTags } from "./types"

export type TPublicService = {
  name: string
  description: string | null
  logoUrl: string | null
  slug: string
}

export type TPublicListEntriesResponse = {
  entries: TChangelogEntryWithTags[]
  total: number
  page: number
  totalPages: number
  service: TPublicService
}

// ── API Functions ────────────────────────────────────────

export async function publicListEntries(
  serviceSlug: string,
  page = 1,
): Promise<TPublicListEntriesResponse> {
  const params = new URLSearchParams({ page: String(page) })
  return publicFetch<TPublicListEntriesResponse>(
    `/changelog/public/${encodeURIComponent(serviceSlug)}?${params}`,
  )
}

export async function publicGetEntry(
  serviceSlug: string,
  entrySlug: string,
): Promise<TChangelogEntryWithTags> {
  return publicFetch<TChangelogEntryWithTags>(
    `/changelog/public/${encodeURIComponent(serviceSlug)}/${encodeURIComponent(entrySlug)}`,
  )
}

export async function publicSubscribe(
  serviceSlug: string,
  email: string,
): Promise<{ message: string }> {
  return publicFetch<{ message: string }>(
    `/changelog/public/${encodeURIComponent(serviceSlug)}/subscribe`,
    {
      method: "POST",
      body: JSON.stringify({ email }),
    },
  )
}

export async function publicConfirmSubscription(
  token: string,
): Promise<{ message: string }> {
  return publicFetch<{ message: string }>("/changelog/public/confirm", {
    method: "POST",
    body: JSON.stringify({ token }),
  })
}

export async function publicUnsubscribe(
  token: string,
): Promise<{ message: string }> {
  return publicFetch<{ message: string }>("/changelog/public/unsubscribe", {
    method: "POST",
    body: JSON.stringify({ token }),
  })
}
